import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Autocomplete } from '../components/Autocomplete';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import {
  ArrowLeft, FloppyDisk, CheckCircle, Clock, Lightning, AirplaneTilt, Anchor, ClipboardText, Camera
} from '@phosphor-icons/react';
import { MediaDocumentation } from '../components/MediaDocumentation';

const API = '/api';

function SpecialistField({ field, value, onChange, onAutofill }) {
  const val = value ?? '';

  if (field.type === 'autocomplete') {
    const renderFields = field.render_fields || [];
    return (
      <div className="col-span-2">
        <Label className="text-xs font-medium text-slate-700">{field.label}</Label>
        <Autocomplete
          endpoint={field.endpoint}
          value={val}
          onChange={(v) => onChange(field.key, v)}
          placeholder={`Search ${field.label.toLowerCase()}...`}
          data-testid={`field-${field.key}`}
          onSelect={(item) => {
            // Auto-fill mapped fields
            if (field.autofill && onAutofill) {
              const fills = {};
              Object.entries(field.autofill).forEach(([targetKey, expr]) => {
                // Simple property access: "item.field_name" or "item.a + ' ' + item.b"
                try {
                  if (expr.includes('+')) {
                    const parts = expr.split('+').map(p => {
                      const trimmed = p.trim();
                      if (trimmed.startsWith("item.")) return item[trimmed.replace("item.", "")] || '';
                      return trimmed.replace(/['"]/g, '');
                    });
                    fills[targetKey] = parts.join('');
                  } else {
                    const key = expr.replace('item.', '');
                    fills[targetKey] = String(item[key] ?? '');
                  }
                } catch { fills[targetKey] = ''; }
              });
              onAutofill(fills);
            }
          }}
          renderItem={(item) => (
            <div className="flex justify-between items-center gap-2 w-full">
              <div className="flex items-center gap-2 flex-wrap">
                {renderFields.map((rf, i) => (
                  <span key={rf} className={i === 0 ? 'font-semibold text-blue-700' : 'text-xs text-slate-600'}>
                    {i === 0 ? item[rf] : `${rf}: ${item[rf]}`}
                    {i < renderFields.length - 1 && <span className="text-slate-300 mx-1">|</span>}
                  </span>
                ))}
              </div>
            </div>
          )}
        />
      </div>
    );
  }

  if (field.type === 'text') {
    return (
      <div>
        <Label className="text-xs font-medium text-slate-700">{field.label}</Label>
        <Input value={val} onChange={(e) => onChange(field.key, e.target.value)} className="rounded-lg mt-1" data-testid={`field-${field.key}`} />
      </div>
    );
  }

  if (field.type === 'textarea') {
    return (
      <div>
        <Label className="text-xs font-medium text-slate-700">{field.label}</Label>
        <Textarea value={val} onChange={(e) => onChange(field.key, e.target.value)} className="rounded-lg mt-1 min-h-[80px]" data-testid={`field-${field.key}`} />
      </div>
    );
  }

  if (field.type === 'number') {
    return (
      <div>
        <Label className="text-xs font-medium text-slate-700">{field.label}</Label>
        <Input type="number" value={val} onChange={(e) => onChange(field.key, e.target.value)} className="rounded-lg mt-1" data-testid={`field-${field.key}`} />
      </div>
    );
  }

  if (field.type === 'date' || field.type === 'datetime') {
    return (
      <div>
        <Label className="text-xs font-medium text-slate-700">{field.label}</Label>
        <Input type={field.type === 'datetime' ? 'datetime-local' : 'date'} value={val} onChange={(e) => onChange(field.key, e.target.value)} className="rounded-lg mt-1" data-testid={`field-${field.key}`} />
      </div>
    );
  }

  if (field.type === 'select') {
    return (
      <div>
        <Label className="text-xs font-medium text-slate-700">{field.label}</Label>
        <Select value={val || undefined} onValueChange={(v) => onChange(field.key, v)}>
          <SelectTrigger className="rounded-lg mt-1" data-testid={`field-${field.key}`}>
            <SelectValue placeholder="Select..." />
          </SelectTrigger>
          <SelectContent>
            {(field.options || []).map((opt) => (
              <SelectItem key={opt} value={opt}>{opt}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  if (field.type === 'checklist') {
    const checkedItems = Array.isArray(val) ? val : [];
    return (
      <div>
        <Label className="text-xs font-medium text-slate-700 mb-2 block">{field.label}</Label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {(field.items || []).map((item) => (
            <div key={item} className="flex items-center gap-2 p-2 rounded-lg border border-slate-200 hover:bg-slate-50">
              <Checkbox
                checked={checkedItems.includes(item)}
                onCheckedChange={(checked) => {
                  const updated = checked
                    ? [...checkedItems, item]
                    : checkedItems.filter(i => i !== item);
                  onChange(field.key, updated);
                }}
                data-testid={`check-${field.key}-${item.slice(0,20).replace(/\s/g,'-')}`}
              />
              <span className="text-xs text-slate-700">{item}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return null;
}

export default function TaskDetail() {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [task, setTask] = useState(null);
  const [module, setModule] = useState(null);
  const [formData, setFormData] = useState({});
  const [findingsStatus, setFindingsStatus] = useState('not_started');
  const [lastSaved, setLastSaved] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const canEdit = user?.role === 'admin' || user?.role === 'iic' || task?.assigned_to === user?.email;

  const loadData = useCallback(async () => {
    try {
      // Load all tasks to find this one by ID
      const tasksRes = await axios.get(`${API}/tasks`, { withCredentials: true });
      const foundTask = tasksRes.data.find(t => t.id === taskId);
      if (!foundTask) { setLoading(false); return; }
      setTask(foundTask);

      // Load module definition
      const modRes = await axios.get(`${API}/specialist-modules/${encodeURIComponent(foundTask.specialist_required)}?sector=${foundTask.sector}`, { withCredentials: true });
      setModule(modRes.data);

      // Load existing findings
      const findingsRes = await axios.get(`${API}/tasks/${taskId}/findings`, { withCredentials: true });
      if (findingsRes.data?.data) {
        setFormData(findingsRes.data.data);
        setFindingsStatus(findingsRes.data.status || 'not_started');
        setLastSaved(findingsRes.data.last_saved);
      }
    } catch (e) {
      console.error('Failed to load task data:', e);
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleFieldChange = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async (status) => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/tasks/${taskId}/findings`, {
        data: formData,
        status: status || findingsStatus
      }, { withCredentials: true });
      setFindingsStatus(status || findingsStatus);
      setLastSaved(res.data.last_saved);
    } catch (e) {
      alert(e.response?.data?.detail || 'Error saving findings');
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadge = () => {
    const map = {
      not_started: { color: 'bg-slate-200 text-slate-700', label: 'Not Started' },
      in_progress: { color: 'bg-blue-100 text-blue-800', label: 'In Progress' },
      completed: { color: 'bg-green-100 text-green-800', label: 'Completed' }
    };
    const s = map[findingsStatus] || map.not_started;
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${s.color}`}>{s.label}</span>;
  };

  // Count filled fields for progress
  const countFilledFields = () => {
    if (!module) return { filled: 0, total: 0 };
    let total = 0, filled = 0;
    module.sections?.forEach(section => {
      section.fields?.forEach(field => {
        total++;
        const v = formData[field.key];
        if (v && (typeof v === 'string' ? v.trim() : Array.isArray(v) ? v.length > 0 : true)) filled++;
      });
    });
    return { filled, total };
  };

  if (loading) return <div className="flex items-center justify-center h-full"><div className="text-slate-500">Loading module...</div></div>;
  if (!task) return <div className="p-8 text-center text-slate-500">Task not found</div>;
  if (!module) return <div className="p-8 text-center text-slate-500">No specialist module defined for "{task.specialist_required}"</div>;

  const isPhotoVideoModule = task.specialist_required === 'Photo/Video';
  const { filled, total } = countFilledFields();
  const fieldPct = total > 0 ? Math.round((filled / total) * 100) : 0;

  return (
    <div className="max-w-5xl mx-auto" data-testid="task-detail-page">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Button variant="outline" size="sm" className="rounded-lg" onClick={() => navigate(`/investigations/${task.investigation_id}`)} data-testid="back-btn">
          <ArrowLeft size={16} />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-sm font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded">{task.task_code}</span>
            <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>{module.label}</h1>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${task.sector === 'aviation' ? 'bg-blue-100 text-blue-800' : 'bg-cyan-100 text-cyan-800'}`}>
              {task.sector === 'aviation' ? <AirplaneTilt size={12} className="inline mr-1" /> : <Anchor size={12} className="inline mr-1" />}
              {task.sector}
            </span>
            {task.escalated && task.status !== 'completed' && (
              <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-600 text-white flex items-center gap-1">
                <Lightning size={10} /> ESCALATED
              </span>
            )}
            {getStatusBadge()}
          </div>
          <div className="flex items-center gap-4 text-xs text-slate-500 mt-1">
            <span>{task.investigation_number}</span>
            <span>Specialist: {task.specialist_required}</span>
            <span>Start: {task.start_date ? new Date(task.start_date).toLocaleDateString() : '-'}</span>
            <span>Due: {task.due_date ? new Date(task.due_date).toLocaleDateString() : '-'}</span>
            {task.assigned_to_name && <span>Assigned: {task.assigned_to_name}</span>}
            {lastSaved && <span>Last saved: {new Date(lastSaved).toLocaleString()}</span>}
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="bg-white rounded-xl p-4 border border-slate-200 mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-slate-700">Module Completion</span>
          <span className="text-sm text-slate-500">{filled}/{total} fields ({fieldPct}%)</span>
        </div>
        <div className="w-full bg-slate-200 rounded-full h-2">
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 h-2 rounded-full transition-all duration-300" style={{ width: `${fieldPct}%` }} />
        </div>
      </div>

      {/* Module Sections */}
      <div className="space-y-6">
        {module.sections?.map((section, idx) => (
          <div key={idx} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="bg-slate-50 px-5 py-3 border-b border-slate-200">
              <h2 className="text-sm font-bold text-slate-800 uppercase tracking-wider flex items-center gap-2">
                <ClipboardText size={16} className="text-blue-600" />
                {section.title}
              </h2>
            </div>
            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-4">
              {section.fields?.map((field) => {
                const isWide = field.type === 'textarea' || field.type === 'checklist';
                return (
                  <div key={field.key} className={isWide ? 'md:col-span-2' : ''}>
                    <SpecialistField
                      field={field}
                      value={formData[field.key]}
                      onChange={canEdit ? handleFieldChange : () => {}}
                      onAutofill={canEdit ? (fills) => setFormData(prev => ({ ...prev, ...fills })) : undefined}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Photographic & Video Documentation - Media Upload Section */}
      {isPhotoVideoModule && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mt-6 mb-20" data-testid="photo-video-module">
          <div className="bg-gradient-to-r from-purple-50 to-red-50 px-5 py-3 border-b border-slate-200">
            <h2 className="text-sm font-bold text-slate-800 uppercase tracking-wider flex items-center gap-2">
              <Camera size={16} className="text-purple-600" />
              Media Files — Photographic &amp; Video Documentation
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">Upload site photos, video recordings, audio interviews, and drone footage for this investigation.</p>
          </div>
          <div className="p-5">
            <MediaDocumentation investigationId={task.investigation_id} canEdit={canEdit} />
          </div>
        </div>
      )}

      {/* Action Bar */}
      {canEdit && (
        <div className="sticky bottom-0 bg-white border-t border-slate-200 mt-6 -mx-8 px-8 py-4 flex items-center justify-between z-10" data-testid="action-bar">
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Clock size={14} />
            {lastSaved ? `Last saved: ${new Date(lastSaved).toLocaleString()}` : 'Not saved yet'}
          </div>
          <div className="flex gap-3">
            <Button variant="outline" className="rounded-lg" onClick={() => handleSave('in_progress')} disabled={saving} data-testid="save-draft-btn">
              <FloppyDisk size={16} className="mr-2" />
              {saving ? 'Saving...' : 'Save Draft'}
            </Button>
            <Button className="bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg" onClick={() => handleSave('completed')} disabled={saving} data-testid="mark-complete-btn">
              <CheckCircle size={16} className="mr-2" />
              {saving ? 'Saving...' : 'Mark Complete'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
